import {
  getRecurrenceLabel,
  toJobCalendarDateKey,
} from '@/lib/job-list-display'
import type { Job, JobRecurrenceRule } from '@/types'

const MAX_SERIES_OCCURRENCES = 366

export type ProjectedJobOccurrence = {
  seriesRootId: string
  seriesRoot: Job
  scheduledStart: Date
  ruleLabel: string
  isProjected: true
}

export type CalendarMonthGridBounds = {
  monthStart: Date
  monthEnd: Date
  gridStart: Date
  gridEnd: Date
}

export type CalendarDaySchedule = {
  dateKey: string
  materializedJobs: Job[]
  projectedOccurrences: ProjectedJobOccurrence[]
}

function startOfLocalDay(date: Date) {
  return new Date(date.getFullYear(), date.getMonth(), date.getDate(), 0, 0, 0, 0)
}

function endOfLocalDay(date: Date) {
  return new Date(date.getFullYear(), date.getMonth(), date.getDate(), 23, 59, 59, 999)
}

function addMonths(value: Date, months = 1) {
  const monthIndex = value.getMonth() + months
  const year = value.getFullYear() + Math.floor(monthIndex / 12)
  const month = ((monthIndex % 12) + 12) % 12
  const lastDay = new Date(year, month + 1, 0).getDate()
  const day = Math.min(value.getDate(), lastDay)
  return new Date(year, month, day, value.getHours(), value.getMinutes(), value.getSeconds(), value.getMilliseconds())
}

function clampMonthDay(year: number, month: number, monthDay: number) {
  const lastDay = new Date(year, month + 1, 0).getDate()
  return Math.min(monthDay, lastDay)
}

function normalizeWeekdays(weekdays?: number[] | null) {
  const normalized = new Set<number>()
  for (const value of weekdays ?? []) {
    const day = Number(value)
    if (Number.isInteger(day) && day >= 0 && day <= 6) {
      normalized.add(day)
    }
  }
  return Array.from(normalized).sort((left, right) => left - right)
}

function toBackendWeekday(date: Date) {
  return (date.getDay() + 6) % 7
}

function nextWeeklyDaysStart(current: Date, weekdays: number[]) {
  const normalized = normalizeWeekdays(weekdays)
  if (normalized.length === 0) {
    return null
  }

  for (let offset = 1; offset <= 7; offset += 1) {
    const candidate = new Date(current)
    candidate.setDate(candidate.getDate() + offset)
    if (normalized.includes(toBackendWeekday(candidate))) {
      return candidate
    }
  }

  return null
}

function nextMonthlyDayStart(current: Date, monthDay: number) {
  if (!Number.isInteger(monthDay) || monthDay < 1) {
    return null
  }

  let candidate = new Date(
    current.getFullYear(),
    current.getMonth(),
    clampMonthDay(current.getFullYear(), current.getMonth(), monthDay),
    current.getHours(),
    current.getMinutes(),
    current.getSeconds(),
    current.getMilliseconds(),
  )

  if (candidate.getTime() > current.getTime()) {
    return candidate
  }

  const nextMonth = addMonths(new Date(current.getFullYear(), current.getMonth(), 1), 1)
  return new Date(
    nextMonth.getFullYear(),
    nextMonth.getMonth(),
    clampMonthDay(nextMonth.getFullYear(), nextMonth.getMonth(), monthDay),
    current.getHours(),
    current.getMinutes(),
    current.getSeconds(),
    current.getMilliseconds(),
  )
}

export function nextJobOccurrenceStart(
  rule: JobRecurrenceRule | string | undefined,
  current: Date,
  options?: {
    intervalDays?: number | null
    weekdays?: number[] | null
    monthDay?: number | null
  },
): Date | null {
  if (!current || !rule || rule === 'none') {
    return null
  }

  const next = new Date(current)

  if (rule === 'daily') {
    next.setDate(next.getDate() + 1)
    return next
  }
  if (rule === 'weekly') {
    next.setDate(next.getDate() + 7)
    return next
  }
  if (rule === 'bi_weekly') {
    next.setDate(next.getDate() + 14)
    return next
  }
  if (rule === 'monthly') {
    return addMonths(next, 1)
  }
  if (rule === 'every_n_days') {
    const interval = Number(options?.intervalDays ?? 0)
    if (interval <= 0) {
      return null
    }
    next.setDate(next.getDate() + interval)
    return next
  }
  if (rule === 'weekly_days') {
    return nextWeeklyDaysStart(next, options?.weekdays ?? [])
  }
  if (rule === 'monthly_day') {
    return nextMonthlyDayStart(next, Number(options?.monthDay ?? 0))
  }

  return null
}

export function getCalendarMonthGridBounds(month: Date): CalendarMonthGridBounds {
  const monthStart = startOfLocalDay(new Date(month.getFullYear(), month.getMonth(), 1))
  const monthEnd = endOfLocalDay(new Date(month.getFullYear(), month.getMonth() + 1, 0))
  const gridStart = startOfLocalDay(new Date(monthStart))
  const dayIndex = gridStart.getDay()
  const offsetToMonday = (dayIndex + 6) % 7
  gridStart.setDate(gridStart.getDate() - offsetToMonday)

  const gridEnd = endOfLocalDay(new Date(gridStart))
  gridEnd.setDate(gridEnd.getDate() + 41)

  return { monthStart, monthEnd, gridStart, gridEnd }
}

export function resolveSeriesRootJob(job: Job, jobsById: Map<string, Job>): Job {
  let current = job
  const visited = new Set<string>()

  while (true) {
    const currentId = String(current.id)
    if (visited.has(currentId)) {
      break
    }
    visited.add(currentId)

    const parentId = String(current.recurrence_parent_info?.id ?? current.recurrence_parent ?? '').trim()
    if (!parentId) {
      break
    }

    const parent = jobsById.get(parentId)
    if (!parent) {
      break
    }
    current = parent
  }

  return current
}

function parseJobScheduleDate(job: Job): Date | null {
  const value = job.start_date ?? job.scheduled_start
  if (!value) {
    return null
  }

  const parsed = new Date(value)
  return Number.isFinite(parsed.getTime()) ? parsed : null
}

function parseRecurrenceEndDate(value?: string | null) {
  if (!value) {
    return null
  }

  const parsed = new Date(`${value}T23:59:59`)
  return Number.isFinite(parsed.getTime()) ? parsed : null
}

function buildJobsById(jobs: Job[]) {
  const map = new Map<string, Job>()
  for (const job of jobs) {
    map.set(String(job.id), job)
  }
  return map
}

function collectSeriesMaterializedDateKeys(seriesRoot: Job, jobs: Job[], jobsById: Map<string, Job>) {
  const keys = new Set<string>()
  const rootId = String(seriesRoot.id)
  const queue = [seriesRoot]

  for (const job of jobs) {
    if (resolveSeriesRootJob(job, jobsById).id === seriesRoot.id) {
      queue.push(job)
    }
  }

  const seen = new Set<string>()
  for (const job of queue) {
    const jobId = String(job.id)
    if (seen.has(jobId)) {
      continue
    }
    seen.add(jobId)

    const scheduleDate = parseJobScheduleDate(job)
    if (scheduleDate) {
      keys.add(toJobCalendarDateKey(scheduleDate))
    }

    if (jobId !== rootId && job.recurrence_parent_info?.scheduled_start) {
      const parentSchedule = new Date(job.recurrence_parent_info.scheduled_start)
      if (Number.isFinite(parentSchedule.getTime())) {
        keys.add(toJobCalendarDateKey(parentSchedule))
      }
    }
  }

  return keys
}

export function expandSeriesOccurrenceStarts(
  seriesRoot: Job,
  rangeStart: Date,
  rangeEnd: Date,
): Date[] {
  const rule = seriesRoot.recurrence_rule
  const anchor = parseJobScheduleDate(seriesRoot)
  if (!rule || rule === 'none' || !anchor) {
    return anchor && anchor >= rangeStart && anchor <= rangeEnd ? [anchor] : []
  }

  const recurrenceEnd = parseRecurrenceEndDate(seriesRoot.recurrence_end)
  const occurrenceCount = Number(seriesRoot.recurrence_count ?? 0) || null
  const openEnded = recurrenceEnd == null && occurrenceCount == null
  const maxSteps = occurrenceCount ?? MAX_SERIES_OCCURRENCES

  const allOccurrences: Date[] = []
  let current: Date | null = anchor

  for (let step = 0; step < maxSteps && current; step += 1) {
    if (recurrenceEnd && current.getTime() > recurrenceEnd.getTime()) {
      break
    }

    if (openEnded) {
      if (current.getTime() > rangeEnd.getTime()) {
        break
      }
      if (current.getTime() >= rangeStart.getTime()) {
        allOccurrences.push(new Date(current))
      }
    } else if (current.getTime() >= rangeStart.getTime() && current.getTime() <= rangeEnd.getTime()) {
      allOccurrences.push(new Date(current))
    }

    current = nextJobOccurrenceStart(rule, current, {
      intervalDays: seriesRoot.recurrence_interval_days,
      weekdays: seriesRoot.recurrence_weekdays,
      monthDay: seriesRoot.recurrence_month_day,
    })
  }

  return allOccurrences
}

export function getCalendarGridDays(visibleMonth: Date): Date[] {
  const { gridStart } = getCalendarMonthGridBounds(visibleMonth)
  const days: Date[] = []

  for (let index = 0; index < 42; index += 1) {
    const day = new Date(gridStart)
    day.setDate(day.getDate() + index)
    days.push(day)
  }

  return days
}

export function buildJobCalendarSchedule(
  jobs: Job[],
  visibleMonth: Date,
): CalendarDaySchedule[] {
  const { gridStart, gridEnd } = getCalendarMonthGridBounds(visibleMonth)
  const jobsById = buildJobsById(jobs)
  const dayMap = new Map<string, CalendarDaySchedule>()

  const ensureDay = (dateKey: string) => {
    const existing = dayMap.get(dateKey)
    if (existing) {
      return existing
    }

    const created: CalendarDaySchedule = {
      dateKey,
      materializedJobs: [],
      projectedOccurrences: [],
    }
    dayMap.set(dateKey, created)
    return created
  }

  for (const job of jobs) {
    const scheduleDate = parseJobScheduleDate(job)
    if (!scheduleDate) {
      continue
    }

    const dateKey = toJobCalendarDateKey(scheduleDate)
    if (scheduleDate < gridStart || scheduleDate > gridEnd) {
      continue
    }

    ensureDay(dateKey).materializedJobs.push(job)
  }

  const seriesRoots = new Map<string, Job>()
  for (const job of jobs) {
    if (!job.recurrence_rule || job.recurrence_rule === 'none') {
      continue
    }

    const root = resolveSeriesRootJob(job, jobsById)
    seriesRoots.set(String(root.id), root)
  }

  for (const seriesRoot of seriesRoots.values()) {
    const materializedDateKeys = collectSeriesMaterializedDateKeys(seriesRoot, jobs, jobsById)
    const projectedStarts = expandSeriesOccurrenceStarts(seriesRoot, gridStart, gridEnd)
    const ruleLabel = getRecurrenceLabel(seriesRoot) ?? 'Recurring'

    for (const scheduledStart of projectedStarts) {
      const dateKey = toJobCalendarDateKey(scheduledStart)
      if (materializedDateKeys.has(dateKey)) {
        continue
      }

      ensureDay(dateKey).projectedOccurrences.push({
        seriesRootId: String(seriesRoot.id),
        seriesRoot,
        scheduledStart,
        ruleLabel,
        isProjected: true,
      })
    }
  }

  for (const day of dayMap.values()) {
    day.materializedJobs.sort((left, right) => {
      const leftTime = parseJobScheduleDate(left)?.getTime() ?? 0
      const rightTime = parseJobScheduleDate(right)?.getTime() ?? 0
      return leftTime - rightTime
    })
    day.projectedOccurrences.sort((left, right) => left.scheduledStart.getTime() - right.scheduledStart.getTime())
  }

  return Array.from(dayMap.values()).sort((left, right) => left.dateKey.localeCompare(right.dateKey))
}

export function getCalendarDaySchedule(
  schedule: CalendarDaySchedule[],
  selectedDate: Date,
): CalendarDaySchedule {
  const dateKey = toJobCalendarDateKey(selectedDate)
  return schedule.find((day) => day.dateKey === dateKey) ?? {
    dateKey,
    materializedJobs: [],
    projectedOccurrences: [],
  }
}

export function mergeJobsById(...groups: Job[][]) {
  const map = new Map<string, Job>()
  for (const group of groups) {
    for (const job of group) {
      map.set(String(job.id), job)
    }
  }
  return Array.from(map.values())
}
